import {
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import maplibregl, { Map as MapLibreMap, MapMouseEvent, type PaddingOptions } from "maplibre-gl";
import {
  createBlankMapStyle,
  wondermapsDefault,
  createTieredPOILayers,
  buildPoiLabelTextField,
  DEFAULT_POI_LABEL,
  POI_TIER_LAYER_IDS,
  featureCollection,
  USER_LOCATION_STYLE,
  USER_LOCATION_ACCURACY_STYLE,
  TEMP_CLOSED_OPACITY_EXPR,
  type ThemeConfig,
  type DynamicCategory,
} from "@wondermaps/maps";
import {
  loadTextures,
  loadLayerSprites,
  loadPropSprites,
} from "@wondermaps/maps/browser";
import type { FeatureCollection, Geometry, Point } from "geojson";
import {
  POI_LAYER_IDS,
  getMeterToPixels,
  fitMapBounds,
  computeMaxBounds,
  addDynamicMarkers,
  addSponsorRingSprites,
  addLayersToMap,
  addLayersInChunks,
  addLabelsToMap,
  setupPoiEvents,
  useDeckOverlay,
  type SponsorTierInfo,
} from "./map";
import { useStickerAtlas } from "./map/useStickerAtlas";
import type { VisitorIllustration } from "./map/useDeckOverlay";
import { useMapLocaleSync } from "../hooks/visitor/useMapLocaleSync";
import {
  addSelectedPoiLayers,
  updateSelectedPoi,
  removeSelectedPoiLayers,
  type SelectedPoiFeature,
} from "./map/selectedPoiLayer";
import { useTranslation } from "react-i18next";
import { Button } from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap } from "@fortawesome/pro-solid-svg-icons/faMap";
import { analytics } from "../lib/analytics";
import { api } from "../lib/api";
import { registerThemeIcons } from "../data/fontawesome-icons";
import type { SponsorTier, SponsorRef } from "@wondermaps/db";
import { LEGACY_TIER_COLORS } from "@wondermaps/maps";
import type { POICategory } from "./admin-map/types";

// Re-export for consumers that import from MapCanvas
export type { POICategory };

// Types matching API response
export interface Layer {
  id: string;
  name: string;
  layerType: string;
  geom: Geometry | null;
  styleJson: unknown;
  sortOrder: string | null;
}

export interface POITag {
  slug: string;
  name: string;
  color: string | null;
}

export interface POI {
  id: string;
  mapId: string;
  name: string;
  slug: string | null;
  nameTranslations?: Record<string, string> | null;
  description: string | null;
  descriptionRich: unknown;
  categoryId: string | null;
  categorySlug: string;
  category: POICategory | null;
  tags: POITag[];
  location: Point | null;
  propertiesJson: unknown;
  // Rich content fields
  photos: import("@wondermaps/db").POIPhoto[];
  status: string | null;
  statusNote: string | null;
  seasonDates: { start: string; end: string } | null;
  hours: import("@wondermaps/db").HoursConfig | null;
  links: import("@wondermaps/db").POILink[];
  // Sponsor fields
  sponsorTier: SponsorTier | null;
  sponsorRef: SponsorRef | null;
  // POI-level rating toggle
  ratingsEnabled: boolean;
  // Per-POI override for name-label rendering: null=follow theme, true=always, false=never
  showNameLabel: boolean | null;
}

export interface UserLocation {
  lng: number;
  lat: number;
  accuracy: number;
}

export interface MapLabel {
  id: string;
  text: string;
  location: Point | null;
  type: "region" | "fixed";
  fontSize: number;
  fontWeight: "normal" | "bold";
  color: string;
  rotation: number;
  minZoom: number;
  maxZoom: number;
  opacity: number;
  backgroundColor: string | null;
  haloWidth: number | null;
  // Sponsor logo fields (from linked sponsor with showSponsorLogo=true)
  sponsorId?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorTierColor?: string | null;
}

export interface MapCanvasProps {
  layers: Layer[];
  pois: POI[];
  labels?: MapLabel[];
  categories?: DynamicCategory[];
  defaultCenter?: [number, number]; // [lng, lat] - initial map center
  defaultZoom?: number; // initial zoom level
  minZoom?: number; // map min zoom (derived from mapSize)
  maxZoom?: number; // map max zoom (derived from mapSize)
  zoomOffset?: number; // zoom offset for map size presets
  onMapClick?: (e: MapMouseEvent) => void;
  onPOIClick?: (poi: POI) => void;
  interactive?: boolean;
  className?: string;
  cursorStyle?: string;
  themeConfig?: ThemeConfig;
  userLocation?: UserLocation;
  onContextLost?: () => void;
  /** When a specific category is selected, disable zoom tiers so all POIs show at any zoom */
  selectedCategory?: string | null;
  /** Org/map slugs for lazy-loading shadow layers on zoom 14 */
  orgSlug?: string;
  mapSlug?: string;
  /** Highlight a selected POI with ring + always-visible icon */
  selectedPoiId?: string | null;
  /**
   * Custom map sticker placements (plan 120, PR5). Threaded straight through
   * to the Deck.gl overlay — visitor render is `pickable: false` so clicks
   * fall through to the POI/path layers underneath.
   */
  illustrations?: VisitorIllustration[];
}

// Re-export visitor sticker payload type so consumers (VisitorLayout, hooks)
// don't have to reach into the overlay module.
export type { VisitorIllustration };

export interface MapCanvasRef {
  flyTo: (coords: [number, number], zoom?: number, padding?: PaddingOptions) => void;
  getMap: () => MapLibreMap | null;
}

// Fallback center - Geographic center of US (used if no defaultCenter provided)
const FALLBACK_CENTER: [number, number] = [-98.5795, 39.8283];
const FALLBACK_ZOOM = 15;

/** Collect unique sponsor tier info from POI data for ring sprite generation */
function collectSponsorTiers(pois: POI[]): SponsorTierInfo[] {
  // Gather unique sortOrders to compute positions
  const sortOrders = new Set<number>();
  const colorBySortOrder = new Map<number, string>();

  for (const poi of pois) {
    if (poi.sponsorRef?.sponsorTierColor) {
      sortOrders.add(poi.sponsorRef.sponsorTierSortOrder);
      colorBySortOrder.set(
        poi.sponsorRef.sponsorTierSortOrder,
        poi.sponsorRef.sponsorTierColor
      );
    } else if (poi.sponsorTier) {
      const legacy = LEGACY_TIER_COLORS[poi.sponsorTier];
      if (legacy) {
        // Use negative sortOrders for legacy tiers to avoid collision
        const legacySortOrder = -(legacy.position);
        sortOrders.add(legacySortOrder);
        colorBySortOrder.set(legacySortOrder, legacy.color);
      }
    }
  }

  if (sortOrders.size === 0) return [];

  // Highest sortOrder = position 1
  const sorted = [...sortOrders].sort((a, b) => b - a);
  return sorted.map((so, i) => ({
    color: colorBySortOrder.get(so)!,
    position: i + 1,
  }));
}

export const MapCanvas = forwardRef<MapCanvasRef, MapCanvasProps>(
  function MapCanvas(
    {
      layers,
      pois,
      labels = [],
      categories = [],
      defaultCenter,
      defaultZoom,
      minZoom: minZoomProp,
      maxZoom: maxZoomProp,
      zoomOffset = 0,
      onMapClick,
      onPOIClick,
      interactive = true,
      className = "",
      cursorStyle,
      themeConfig,
      userLocation,
      onContextLost,
      selectedCategory,
      orgSlug,
      mapSlug,
      selectedPoiId,
      illustrations,
    },
    ref
  ) {
    const { t, i18n } = useTranslation("map");
    // Use passed theme config or fall back to default
    const currentTheme = themeConfig ?? wondermapsDefault;
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapLibreMap | null>(null);
    const poisRef = useRef<POI[]>(pois);
    const layersRef = useRef<Layer[]>(layers);
    layersRef.current = layers;
    const onMapClickRef = useRef(onMapClick);
    const [zoomLevel, setZoomLevel] = useState<number | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [contextLost, setContextLost] = useState(false);
    // Gate map init until @font-face resolves. TinySDF rasterizes glyphs once
    // and won't re-rasterize when the real font arrives (maplibre-gl-js#7307);
    // labels would render in browser-fallback fonts permanently otherwise.
    const [fontsReady, setFontsReady] = useState(false);
    const isMapValidRef = useRef(true);
    const chunksCompleteRef = useRef(false);

    // Sticker atlas (plan 120, PR5). Loads `/sprites/sticker-icons*` once
    // per session; Workbox precaches it so offline reload still renders.
    // Hook is a no-op until the assets resolve — overlay falls back to
    // skipping the sticker layers when atlas is null.
    const stickerAtlas = useStickerAtlas();

    // Deck.gl overlay for smooth prop scaling + sticker rendering (visitor)
    useDeckOverlay({
      map: mapRef.current,
      layers,
      theme: currentTheme,
      isMapReady: isMapReady && isMapValidRef.current,
      zoomOffset,
      illustrations,
      stickerAtlas: stickerAtlas
        ? { atlasUrl: stickerAtlas.atlasUrl, mapping: stickerAtlas.mapping }
        : null,
    });

    // Patch POI tier-layer `text-field` on locale change (no GeoJSON refetch).
    // Builder closure keeps the hook decoupled from PoiLabelConfig shape.
    useMapLocaleSync(
      isMapReady ? mapRef.current : null,
      POI_TIER_LAYER_IDS,
      (locale) => buildPoiLabelTextField(currentTheme.poiLabel ?? DEFAULT_POI_LABEL, locale)
    );

    // Expose flyTo method and map instance via ref
    // Default flyTo zoom level - balances detail visibility with context
    const DEFAULT_FLYTO_ZOOM = 17.5;

    useImperativeHandle(ref, () => ({
      flyTo: (coords: [number, number], zoom?: number, padding?: PaddingOptions) => {
        mapRef.current?.flyTo({
          center: coords,
          zoom: zoom ?? DEFAULT_FLYTO_ZOOM,
          duration: 1000,
          ...(padding && { padding }),
        });
      },
      getMap: () => mapRef.current,
    }));

    // Keep refs in sync with latest props
    useEffect(() => {
      poisRef.current = pois;
    }, [pois]);

    useEffect(() => {
      onMapClickRef.current = onMapClick;
    }, [onMapClick]);

    // Category to tier mapping for sort order (tier 1 = most important, renders on top)
    const getCategoryTier = (category: string | null): number => {
      const tier1 = ["entrance", "attraction", "animal"];
      const tier2 = ["restroom", "food", "gift_shop", "emergency"];
      // tier3 = everything else
      if (tier1.includes(category || "")) return 1;
      if (tier2.includes(category || "")) return 2;
      return 3;
    };

    // Convert POIs to GeoJSON FeatureCollection
    // Each POI gets a unique zIndex for proper stacking (no overlaps visible)
    // zIndex = (4 - tier) * 10000 + indexInArray → tier 1 on top, each POI unique
    const poisToFeatureCollection = useCallback(
      (pois: POI[]): FeatureCollection<Point> => {
        // Compute sponsor positions from unique sortOrders (highest sortOrder = position 1)
        const uniqueSortOrders = [
          ...new Set(
            pois
              .filter((p) => p.sponsorRef?.sponsorTierColor)
              .map((p) => p.sponsorRef!.sponsorTierSortOrder)
          ),
        ].sort((a, b) => b - a);
        const sortOrderToPosition = new Map(
          uniqueSortOrders.map((so, i) => [so, i + 1])
        );

        const features = pois
          .filter((poi) => poi.location !== null)
          .map((poi) => {
            // Resolve sponsor properties: prefer sponsorRef, fall back to legacy sponsorTier
            let sponsorProps: Record<string, string | number> = {};
            if (poi.sponsorRef?.sponsorTierColor) {
              const position =
                sortOrderToPosition.get(poi.sponsorRef.sponsorTierSortOrder) ?? 3;
              sponsorProps = {
                sponsorColorKey: poi.sponsorRef.sponsorTierColor.replace("#", ""),
                sponsorPosition: position,
              };
            } else if (poi.sponsorTier) {
              const legacy = LEGACY_TIER_COLORS[poi.sponsorTier];
              if (legacy) {
                sponsorProps = {
                  sponsorColorKey: legacy.color.replace("#", ""),
                  sponsorPosition: legacy.position,
                };
              }
            }

            // Flatten nameTranslations to flat name_<locale> properties for
            // MapLibre text-field expressions. Plan 118: omit absent locales —
            // the coalesce in the layer's text-field expression falls through
            // to `name` (source language) when a locale key is missing.
            const nameTranslationProps: Record<string, string> = {};
            if (poi.nameTranslations) {
              for (const [locale, translated] of Object.entries(
                poi.nameTranslations
              )) {
                if (translated) nameTranslationProps[`name_${locale}`] = translated;
              }
            }

            return {
              type: "Feature" as const,
              properties: {
                id: poi.id,
                name: poi.name,
                description: poi.description,
                category: poi.categorySlug || "utilities",
                categorySlug: poi.categorySlug || "uncategorized",
                tier: getCategoryTier(poi.categorySlug),
                mapId: poi.mapId,
                ...(poi.status && { status: poi.status }),
                ...sponsorProps,
                ...nameTranslationProps,
                // Per-POI label override. Omit when null/undefined so Layer B's
                // ["typeof", ["get", "showNameLabel"]] === "null" filter matches.
                ...(typeof poi.showNameLabel === "boolean" && {
                  showNameLabel: poi.showNameLabel,
                }),
                // Flatten propertiesJson to primitive values only — MapLibre
                // queryRenderedFeatures breaks on nested objects in feature properties
                ...(typeof poi.propertiesJson === "object" && poi.propertiesJson
                  ? Object.fromEntries(
                      Object.entries(poi.propertiesJson as Record<string, unknown>).filter(
                        ([, v]) => typeof v !== "object" || v === null
                      )
                    )
                  : {}),
              },
              geometry: poi.location!,
            };
          })
          // Sort: tier 3 first (bottom), tier 1 last (top)
          .sort((a, b) => b.properties.tier - a.properties.tier)
          // Add unique zIndex after sorting
          .map((feature, idx) => ({
            ...feature,
            properties: {
              ...feature.properties,
              zIndex: (4 - feature.properties.tier) * 10000 + idx,
            },
          }));
        return featureCollection(features) as FeatureCollection<Point>;
      },
      []
    );

    // Preload map fonts (both weight instances of the variable file).
    useEffect(() => {
      let cancelled = false;
      Promise.all([
        document.fonts.load("400 16px 'Google Sans Flex Regular'"),
        document.fonts.load("500 16px 'Google Sans Flex Medium'"),
      ])
        .catch((err) => {
          console.warn("Map font preload failed; continuing with fallback", err);
        })
        .finally(() => {
          if (!cancelled) setFontsReady(true);
        });
      return () => {
        cancelled = true;
      };
    }, []);

    // Initialize map
    useEffect(() => {
      if (!fontsReady || !containerRef.current || mapRef.current) return;

      // Visitor map always uses blank canvas (no OSM tiles)
      // No sprite URL needed - we generate dynamic markers at runtime
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: createBlankMapStyle(currentTheme.colors.background),
        center: defaultCenter || FALLBACK_CENTER,
        zoom: defaultZoom || FALLBACK_ZOOM,
        minZoom: minZoomProp ?? 16,
        maxZoom: maxZoomProp ?? 20,
        interactive,
      });

      mapRef.current = map;

      // WebGL context lost handler - match admin pattern (canvas event)
      // Only show fallback if context lost AFTER map was already working
      const canvas = map.getCanvas();
      const handleContextLost = (e: Event) => {
        e.preventDefault();
        console.warn("[Visitor] WebGL context lost");
        const wasReady = isMapValidRef.current;
        isMapValidRef.current = false;
        setIsMapReady(false);
        // Only show fallback UI if map was already working
        if (wasReady) {
          setContextLost(true);
          analytics.track("webgl_context_lost", {
            userAgent: navigator.userAgent,
          });
          onContextLost?.();
        }
      };
      const handleContextRestored = () => {
        console.log("[Visitor] WebGL context restored");
        window.location.reload();
      };
      canvas.addEventListener("webglcontextlost", handleContextLost);
      canvas.addEventListener("webglcontextrestored", handleContextRestored);

      // Track zoom level for debug indicator
      setZoomLevel(map.getZoom());
      map.on("zoom", () => setZoomLevel(map.getZoom()));

      map.on("load", async () => {
        // Register any non-curated theme icons before generating markers
        const themeIconNames = Object.values(currentTheme.poiCategories).map(
          (c) => c.icon
        );
        await registerThemeIcons(themeIconNames);

        // Generate dynamic POI markers (guard: categories may be undefined on first render)
        addDynamicMarkers(map, currentTheme, categories ?? []);

        // Load texture images for fill-pattern usage (blocking — fill patterns need textures)
        await loadTextures(map);

        // Start sprite loading in background — layers show solid fills until sprites arrive
        // MapLibre repaints automatically when addImage completes
        void Promise.all([loadLayerSprites(map), loadPropSprites(map)]);

        // Add layers in chunks to avoid blocking main thread (50 at a time)
        // skipPointScenery: true - props rendered by Deck.gl for smooth scaling
        const layerOpts = { skipPointScenery: true, zoomOffset } as const;

        // Fit bounds to content ONLY if no defaultCenter override
        // If defaultCenter is set, map already initialized at saved position
        if (!defaultCenter) {
          fitMapBounds(map, layers, (minZoomProp ?? 16) + 1);
        }

        // Constrain panning to venue area (visitor map only)
        const maxBounds = computeMaxBounds(layers);
        if (maxBounds) {
          map.setMaxBounds(maxBounds);
        }

        // Mark map as ready after first chunk — map interactive immediately
        setIsMapReady(true);

        // Add geometry layers in chunks (yields between batches of 50, bails on unmount)
        await addLayersInChunks(map, layers, currentTheme, layerOpts, 50, () => isMapValidRef.current);
        chunksCompleteRef.current = true;

        // Add labels and POIs AFTER geometry layers so they render on top
        addLabelsToMap(map, labels, zoomOffset);

        const poiFeatures = poisToFeatureCollection(poisRef.current);
        map.addSource("pois", {
          type: "geojson",
          data: poiFeatures,
        });

        // Plan 118 refactor: tier layers now combine icon + label into one
        // symbol per POI. Tier minzooms govern both. No separate label layers.
        const tieredPoiLayers = createTieredPOILayers(
          "pois",
          "pois",
          currentTheme,
          categories,
          i18n.language
        );
        tieredPoiLayers.forEach((layer) => map.addLayer(layer));

        // Setup POI click and hover handlers
        setupPoiEvents(map, poisRef, onPOIClick, onMapClickRef);

        // Lazy-load sponsor ring sprites post-init
        // Collect unique tier colors from POI data, generate ring sprites after map idle
        const sponsorTiers = collectSponsorTiers(poisRef.current);
        if (sponsorTiers.length > 0) {
          const generateRings = () => {
            if (!isMapValidRef.current) return;
            addSponsorRingSprites(map, currentTheme, categories ?? [], sponsorTiers);
          };
          if ("requestIdleCallback" in window) {
            (window as Window).requestIdleCallback(generateRings, { timeout: 500 });
          } else {
            setTimeout(generateRings, 100);
          }
        }

        // Selected POI highlight layer (ring + always-visible icon)
        const targetDiameter = currentTheme.poi.circleRadius * 2;
        const circleInSprite = 40;
        addSelectedPoiLayers(map, categories ?? [], targetDiameter / circleInSprite);
      });

      return () => {
        canvas.removeEventListener("webglcontextlost", handleContextLost);
        canvas.removeEventListener("webglcontextrestored", handleContextRestored);
        removeSelectedPoiLayers(map);
        setIsMapReady(false);
        map.remove();
        mapRef.current = null;
      };
      // Runs once on mount (after fonts ready) - layers/pois updates handled separately
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fontsReady]);

    // Update POIs when they change with fade animation
    // Use ref to cancel stale timeouts (prevents race with category effect)
    const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevPoisRef = useRef<POI[]>(pois);
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded() || !isMapValidRef.current) return;

      // Skip if pois array is the same reference (no actual data change)
      // Prevents flash when parent re-renders without changing POI data
      if (pois === prevPoisRef.current) return;
      prevPoisRef.current = pois;

      const source = map.getSource("pois") as
        | maplibregl.GeoJSONSource
        | undefined;
      if (!source) return;

      // Cancel any in-flight fade from previous render
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }

      // Fade out existing markers
      POI_LAYER_IDS.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, "icon-opacity", 0);
        }
      });

      // Wait for fade out, then update data and fade in
      fadeTimeoutRef.current = setTimeout(() => {
        source.setData(poisToFeatureCollection(pois));

        // Generate ring sprites for any new sponsor colors (hasImage guards prevent duplicates)
        const newTiers = collectSponsorTiers(pois);
        if (newTiers.length > 0) {
          addSponsorRingSprites(map, currentTheme, categories, newTiers);
        }

        // Fade in new markers after data update (restore status-aware expression)
        fadeTimeoutRef.current = setTimeout(() => {
          fadeTimeoutRef.current = null;
          POI_LAYER_IDS.forEach((layerId) => {
            if (map.getLayer(layerId)) {
              map.setPaintProperty(layerId, "icon-opacity", TEMP_CLOSED_OPACITY_EXPR);
            }
          });
        }, 50);
      }, 100);
    }, [pois, poisToFeatureCollection]);

    // Disable zoom tiers when a specific category is selected so all POIs show at any zoom
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded() || !isMapValidRef.current) return;

      const isFiltered = selectedCategory && selectedCategory !== "all";
      const { poiMinZoom } = currentTheme;

      for (const tier of [1, 2, 3] as const) {
        const layerId = `pois-tier${tier}`;
        if (map.getLayer(layerId)) {
          map.setLayerZoomRange(layerId, isFiltered ? 0 : poiMinZoom[tier], 24);
        }
      }
    }, [selectedCategory, currentTheme]);

    // Apply custom cursor style to canvas
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !isMapValidRef.current) return;

      const canvas = map.getCanvas();
      if (cursorStyle) {
        canvas.style.cursor = cursorStyle;
      } else {
        canvas.style.cursor = "";
      }
    }, [cursorStyle]);

    // Lazy-load shadow layers when zoom reaches 14 (shadows only visible at 15+)
    const shadowsLoadedRef = useRef(false);
    const shadowRetriesRef = useRef(0);
    const MAX_SHADOW_RETRIES = 3;
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !isMapReady || !isMapValidRef.current) return;
      if (!orgSlug || !mapSlug) return;
      if (shadowsLoadedRef.current) return;

      const handleZoom = async () => {
        if (shadowsLoadedRef.current) return;
        if (map.getZoom() < 14) return;
        shadowsLoadedRef.current = true;

        try {
          const res = await api.maps.visitor[":orgSlug"][":mapSlug"].shadows.$get({
            param: { orgSlug, mapSlug },
          });
          if (!res.ok) throw new Error(`shadows fetch ${res.status}`);
          const { shadows } = await res.json();

          const shadowLayers: Layer[] = shadows.map((s) => ({
            id: s.id,
            name: s.name,
            layerType: s.layerType,
            geom: s.geom as import("geojson").Geometry | null,
            styleJson: s.styleJson,
            sortOrder: s.sortOrder,
          }));

          if (shadowLayers.length === 0) {
            map.off("zoom", handleZoom);
            return;
          }

          // Wait for addLayersInChunks to finish before adding shadows.
          // "idle" is unreliable — fires between chunk yields before all fills exist.
          const waitForChunks = () => new Promise<void>((resolve) => {
            if (chunksCompleteRef.current) { resolve(); return; }
            const iv = setInterval(() => {
              if (chunksCompleteRef.current || !isMapValidRef.current) {
                clearInterval(iv); resolve();
              }
            }, 50);
          });
          await waitForChunks();

          const m = mapRef.current;
          if (!m || !isMapValidRef.current) {
            map.off("zoom", handleZoom);
            return;
          }

          addLayersToMap(m, shadowLayers, currentTheme, { zoomOffset });

          // Move each shadow type below its matching fill type
          const shadowsByType = new Map<string, string[]>();
          for (const s of shadowLayers) {
            const arr = shadowsByType.get(s.layerType) ?? [];
            arr.push(s.id);
            shadowsByType.set(s.layerType, arr);
          }
          for (const [layerType, shadowIds] of shadowsByType) {
            const firstFillId = layersRef.current
              .filter((l) => l.layerType === layerType)
              .map((l) => `${l.id}-fill`)
              .find((id) => m.getLayer(id));
            if (firstFillId) {
              shadowIds.forEach((id) => {
                if (m.getLayer(id)) m.moveLayer(id, firstFillId);
              });
            }
          }
          // Success — remove listener
          map.off("zoom", handleZoom);
        } catch {
          // Retry up to MAX_SHADOW_RETRIES — listener stays attached
          shadowRetriesRef.current++;
          shadowsLoadedRef.current = false;
          if (shadowRetriesRef.current >= MAX_SHADOW_RETRIES) {
            map.off("zoom", handleZoom);
          }
        }
      };

      // Check current zoom in case already past 14
      if (map.getZoom() >= 14) {
        handleZoom();
      } else {
        map.on("zoom", handleZoom);
      }
      return () => { map.off("zoom", handleZoom); };
    }, [isMapReady, orgSlug, mapSlug, currentTheme, zoomOffset]);

    // Sync map position when defaultCenter/defaultZoom props change after mount
    // (e.g., after admin saves "Set Initial View" and query invalidates)
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !isMapReady || !isMapValidRef.current) return;
      if (!defaultCenter) return;

      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();
      const targetZoom = defaultZoom ?? FALLBACK_ZOOM;

      // Only fly if position actually changed (avoid unnecessary animation)
      const centerChanged =
        Math.abs(currentCenter.lng - defaultCenter[0]) > 0.0001 ||
        Math.abs(currentCenter.lat - defaultCenter[1]) > 0.0001;
      const zoomChanged = Math.abs(currentZoom - targetZoom) > 0.1;

      if (centerChanged || zoomChanged) {
        map.flyTo({
          center: defaultCenter,
          zoom: targetZoom,
          duration: 800,
        });
      }
    }, [defaultCenter, defaultZoom, isMapReady]);

    // Manage user location source and layers
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded() || !isMapValidRef.current) return;

      const sourceId = "user-location";
      const dotLayerId = USER_LOCATION_STYLE.id;
      const accuracyLayerId = USER_LOCATION_ACCURACY_STYLE.id;

      if (userLocation) {
        // Convert accuracy (meters) to pixels at current zoom
        const accuracyRadius = getMeterToPixels(
          map,
          userLocation.lat,
          userLocation.accuracy
        );

        const geojsonData: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: { accuracyRadius },
              geometry: {
                type: "Point",
                coordinates: [userLocation.lng, userLocation.lat],
              },
            },
          ],
        };

        const source = map.getSource(sourceId) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (source) {
          // Update existing source
          source.setData(geojsonData);
        } else {
          // Add source and layers
          map.addSource(sourceId, { type: "geojson", data: geojsonData });
          // Accuracy circle first (below dot)
          map.addLayer(USER_LOCATION_ACCURACY_STYLE);
          // Blue dot on top
          map.addLayer(USER_LOCATION_STYLE);
        }
      } else {
        // Remove layers and source when userLocation is undefined
        if (map.getLayer(dotLayerId)) map.removeLayer(dotLayerId);
        if (map.getLayer(accuracyLayerId)) map.removeLayer(accuracyLayerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      }
    }, [userLocation]);

    // Update selected POI highlight layer
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !isMapReady || !map.isStyleLoaded() || !isMapValidRef.current) return;

      if (!selectedPoiId) {
        updateSelectedPoi(map, null);
        return;
      }

      const poi = poisRef.current.find((p) => p.id === selectedPoiId);
      if (!poi?.location) {
        updateSelectedPoi(map, null);
        return;
      }

      // Resolve category color from categories prop
      const catColor = categories.find((c) => c.slug === poi.categorySlug)?.color ?? "#888888";

      // Build sponsor props — compute positional rank (1-3) from sortOrders
      let sponsorColorKey: string | undefined;
      let sponsorPosition: number | undefined;
      if (poi.sponsorRef?.sponsorTierColor) {
        sponsorColorKey = poi.sponsorRef.sponsorTierColor.replace("#", "");
        // Compute position: count unique sortOrders higher than this POI's
        const uniqueSortOrders = [
          ...new Set(
            poisRef.current
              .filter((p) => p.sponsorRef?.sponsorTierColor)
              .map((p) => p.sponsorRef!.sponsorTierSortOrder)
          ),
        ].sort((a, b) => b - a);
        const rank = uniqueSortOrders.indexOf(poi.sponsorRef.sponsorTierSortOrder);
        sponsorPosition = rank >= 0 ? rank + 1 : 3;
      } else if (poi.sponsorTier) {
        const legacy = LEGACY_TIER_COLORS[poi.sponsorTier];
        if (legacy) {
          sponsorColorKey = legacy.color.replace("#", "");
          sponsorPosition = legacy.position;
        }
      }

      const feature: SelectedPoiFeature = {
        id: poi.id,
        location: poi.location,
        categorySlug: poi.categorySlug,
        categoryColor: catColor,
        sponsorColorKey,
        sponsorPosition,
        status: poi.status,
      };
      updateSelectedPoi(map, feature);
    }, [selectedPoiId, categories, isMapReady]);

    // Track the categories snapshot from map.on("load") so we only rebuild layers
    // when categories ACTUALLY change (not on the initial undefined→array transition)
    const initialCategoriesRef = useRef<typeof categories | null>(null);

    // Recreate POI layers when categories change (e.g., visibility toggle, CRUD)
    // This ensures custom categories render correctly after changes
    useEffect(() => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded() || !isMapValidRef.current) return;
      if (!categories?.length) return;

      // Capture initial categories on first valid run (matches map.on("load") layers)
      if (initialCategoriesRef.current === null) {
        initialCategoriesRef.current = categories;
        return;
      }

      // Skip if same reference (no actual change)
      if (categories === initialCategoriesRef.current) return;
      initialCategoriesRef.current = categories;

      // Remove existing POI layers
      for (const layerId of POI_LAYER_IDS) {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      }

      // Recreate with current categories
      const poiLayers = createTieredPOILayers("pois", "pois", currentTheme, categories);
      poiLayers.forEach((layer) => map.addLayer(layer));
    }, [categories, currentTheme]);

    // WebGL context lost fallback UI
    if (contextLost) {
      return (
        <div className={`relative h-full w-full ${className}`}>
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100">
            <FontAwesomeIcon icon={faMap} className="h-12 w-12 text-slate-400 mb-4" />
            <p className="text-slate-600 mb-2">{t("contextLost.title")}</p>
            <p className="text-slate-500 text-sm mb-4 text-center px-4">
              {t("contextLost.description")}
            </p>
            <div className="flex gap-2">
              {onContextLost && (
                <Button variant="outline" onClick={onContextLost}>
                  {t("contextLost.listView")}
                </Button>
              )}
              <Button onClick={() => window.location.reload()}>
                {t("contextLost.retry")}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`relative h-full w-full ${className}`}>
        {/* Visually hidden instructions for screen reader users */}
        <span className="sr-only">
          This interactive map is not fully accessible. Use the list view button
          above for keyboard and screen reader navigation.
        </span>
        <div
          role="application"
          aria-label="Interactive map. Use list view for accessible navigation."
          className="h-full w-full"
        >
          <div
            ref={containerRef}
            className="h-full w-full"
            data-testid="map-canvas"
          />
        </div>
        {/* Debug zoom indicator — localhost only */}
        {typeof window !== "undefined" && window.location.hostname === "localhost" && zoomLevel !== null && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded pointer-events-none select-none z-10">
            z{zoomLevel.toFixed(2)}
          </div>
        )}
      </div>
    );
  }
);
